<template>
  <div
    class="zone-map"
    :class="{ 'zone-map--square': square }"
    :style="square ? {} : { height: height + 'px' }"
  >
    <div
      ref="mapEl"
      class="zone-map__canvas"
    />
    <div
      v-if="loading"
      class="zone-map__loading"
    >
      <Icon
        type="md-globe"
        class="zone-map__placeholder-icon"
      />
    </div>
  </div>
</template>

<script>
// Highlights the countries of a zone on a world map (MapLibre GL). Reuses the
// same world-boundaries GeoJSON as the main map (features keyed by `iso_3`).
const BOUNDARIES_URL = "/ressources/data/layers/world-boundaries.geojson";
let boundariesCache = null; // shared across instances (fetched once)

// Serialize map creation across all thumbnails so they render one-by-one
// (smooth UX) instead of spawning many WebGL contexts at once.
let mapQueue = Promise.resolve();
function enqueueMapInit(fn) {
  mapQueue = mapQueue.then(fn).catch(() => {});
  return mapQueue;
}

export default {
  name: "ZoneMap",
  props: {
    // ISO alpha-2 codes of the zone's countries.
    countries: { type: Array, default: () => [] },
    // Map height (px). Use a small value for card thumbnails.
    height: { type: Number, default: 300 },
    // Disable pan/zoom for static thumbnails.
    interactive: { type: Boolean, default: true },
    // Render as a 1:1 square (ignores `height`).
    square: { type: Boolean, default: false },
  },
  data() {
    return { loading: true, map: null, mapReady: false, visible: false };
  },
  computed: {
    // Convert the zone's alpha-2 codes to the alpha-3 codes used by the GeoJSON.
    selectedIso3() {
      const list = this.worldInfoStore.countryList || [];
      const byIso2 = {};
      list.forEach((c) => {
        if (c.iso && c.iso3) byIso2[c.iso] = c.iso3;
      });
      return new Set(
        (this.countries || [])
          .map((iso2) => byIso2[iso2])
          .filter(Boolean)
      );
    },
  },
  watch: {
    // When the zone's countries change (e.g. after an edit), update the layer
    // in place instead of leaving a stale map — and refit to the new selection.
    countries: {
      handler() {
        if (this.mapReady) this.refreshData();
      },
      deep: true,
    },
  },
  mounted() {
    // Lazy: only build the map once this thumbnail scrolls into view. This keeps
    // the page responsive (maps render one-by-one as you scroll) and avoids
    // spawning many WebGL contexts at once.
    if (window.IntersectionObserver) {
      this._io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this._io.disconnect();
            this._io = null;
            this.visible = true;
            this.startWhenReady();
          }
        },
        { rootMargin: "100px" }
      );
      this._io.observe(this.$el);
    } else {
      this.visible = true;
      this.startWhenReady();
    }
  },
  beforeDestroy() {
    this._destroyed = true;
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }
    if (this._waitTimer) {
      clearInterval(this._waitTimer);
      this._waitTimer = null;
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    // Wait until the country list is loaded (it loads async, so on a direct
    // refresh it may be empty at mount → no ISO mapping → blank selection),
    // then init the map. Gives up after ~10s.
    startWhenReady() {
      const ready = () => (this.worldInfoStore.countryList || []).length > 0;
      const go = () => enqueueMapInit(() => new Promise((resolve) => {
        this.$nextTick(() => this.initMap(resolve));
      }));
      if (ready()) {
        go();
        return;
      }
      let tries = 0;
      this._waitTimer = setInterval(() => {
        tries += 1;
        if (ready() || tries > 100) {
          clearInterval(this._waitTimer);
          this._waitTimer = null;
          go();
        }
      }, 100);
    },
    async loadBoundaries() {
      if (boundariesCache) return boundariesCache;
      const res = await fetch(BOUNDARIES_URL);
      boundariesCache = await res.json();
      return boundariesCache;
    },
    // Tag each feature with whether it belongs to the current zone selection.
    buildTagged(geojson) {
      const set = this.selectedIso3;
      return {
        type: "FeatureCollection",
        features: (geojson.features || []).map((f) => ({
          ...f,
          properties: {
            ...f.properties,
            _selected: set.has(f.properties.iso_3) ? 1 : 0,
          },
        })),
      };
    },
    // Update the map's data + refit after the countries prop changes.
    refreshData() {
      if (!this.map || !boundariesCache) return;
      const tagged = this.buildTagged(boundariesCache);
      const src = this.map.getSource("zone-countries");
      if (src) src.setData(tagged);
      this.map.resize();
      this.fitToSelection(tagged, this.selectedIso3);
    },
    async initMap(done) {
      const finish = typeof done === "function" ? done : () => {};
      const maplibregl = window.maplibregl;
      if (!maplibregl || this._destroyed) {
        this.loading = false;
        return finish();
      }
      let geojson;
      try {
        geojson = await this.loadBoundaries();
      } catch (e) {
        this.loading = false;
        return finish();
      }

      const tagged = this.buildTagged(geojson);
      const set = this.selectedIso3;

      this.map = new maplibregl.Map({
        container: this.$refs.mapEl,
        style: {
          version: 8,
          sources: {},
          layers: [
            { id: "bg", type: "background", paint: { "background-color": "#eaf2fb" } },
          ],
          glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
        },
        center: [10, 20],
        zoom: 0.7,
        attributionControl: false,
        interactive: this.interactive,
      });

      this.map.on("load", () => {
        this.map.addSource("zone-countries", { type: "geojson", data: tagged });
        // Base (unselected) countries — light grey
        this.map.addLayer({
          id: "countries-base",
          type: "fill",
          source: "zone-countries",
          paint: { "fill-color": "#d6dde6", "fill-opacity": 0.6 },
        });
        // Selected countries — brand green
        this.map.addLayer({
          id: "countries-selected",
          type: "fill",
          source: "zone-countries",
          filter: ["==", ["get", "_selected"], 1],
          paint: { "fill-color": "#19be6b", "fill-opacity": 0.85 },
        });
        // Borders
        this.map.addLayer({
          id: "countries-border",
          type: "line",
          source: "zone-countries",
          paint: { "line-color": "#ffffff", "line-width": 0.4 },
        });
        this.loading = false;
        this.mapReady = true;

        // The container (esp. with aspect-ratio) may size after init: force a
        // resize, then fit to the selection so the highlighted countries show.
        this.map.resize();
        this.fitToSelection(tagged, set);

        // Keep the map correctly sized if the container changes later.
        if (window.ResizeObserver && this.$refs.mapEl) {
          this._ro = new ResizeObserver(() => {
            if (this.map) this.map.resize();
          });
          this._ro.observe(this.$refs.mapEl);
        }
        // Release the queue so the next thumbnail can start building.
        finish();
      });
    },
    fitToSelection(geojson, set) {
      const maplibregl = window.maplibregl;
      let hasBounds = false;
      const bounds = new maplibregl.LngLatBounds();
      const extend = (coords) => {
        coords.forEach((ring) => {
          ring.forEach((pt) => {
            if (Array.isArray(pt[0])) {
              pt.forEach((p) => bounds.extend(p));
            } else {
              bounds.extend(pt);
            }
            hasBounds = true;
          });
        });
      };
      geojson.features.forEach((f) => {
        if (f.properties._selected !== 1) return;
        const g = f.geometry;
        if (!g) return;
        if (g.type === "Polygon") extend(g.coordinates);
        else if (g.type === "MultiPolygon") g.coordinates.forEach((poly) => extend(poly));
      });
      if (hasBounds) {
        try {
          this.map.fitBounds(bounds, { padding: 24, maxZoom: 4, duration: 0 });
        } catch (e) {
          /* ignore fit errors on degenerate bounds */
        }
      }
    },
  },
};
</script>

<style scoped>
.zone-map {
  position: relative;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e8f1fb;
}
.zone-map--square {
  aspect-ratio: 1 / 1;
  height: auto;
}
/* Fallback for browsers without aspect-ratio support: square via padding hack
   is avoided; the wrapper sets a fixed width so this keeps it square-ish. */
@supports not (aspect-ratio: 1 / 1) {
  .zone-map--square {
    height: 130px;
  }
}
.zone-map__canvas {
  width: 100%;
  height: 100%;
}
.zone-map__loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eaf2fb;
}
.zone-map__placeholder-icon {
  color: #c3d4e8;
  font-size: 34px;
  animation: zm-pulse 1.4s ease-in-out infinite;
}
@keyframes zm-pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}
</style>
